import captureWorkletUrl from './capture.worklet.js?url';

const targetSampleRate = 16_000;
const processorName = 'varza-capture';

/** What the worklet hands back: the samples, and the loudness it already worked out for them. */
interface CaptureBatch {
  samples: Float32Array;
  rms: number;
}

export interface AudioCaptureOptions {
  maxDurationMs?: number;
  silenceDurationMs?: number;
  onAutoStop?: () => void;
  onLevel?: (level: number) => void;
}

/** Captures mono PCM in memory. Audio is never persisted or sent over the network. */
export class AudioCapture {
  private stream: MediaStream | undefined;
  private context: AudioContext | undefined;
  private source: MediaStreamAudioSourceNode | undefined;
  private tap: AudioWorkletNode | undefined;
  private chunks: Float32Array[] = [];
  private inputSampleRate = targetSampleRate;
  private heardSpeech = false;
  private silentSince = 0;
  private stopTimer: ReturnType<typeof globalThis.setTimeout> | undefined;
  private autoStopped = false;
  private reportedLevel = -1;

  public async start(options: AudioCaptureOptions = {}): Promise<void> {
    if (this.stream) throw new Error('Audio capture is already active.');
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: false,
    });
    this.context = openContext();
    this.inputSampleRate = this.context.sampleRate;
    if (this.context.audioWorklet === undefined) {
      throw new Error('This browser cannot record audio off the main thread.');
    }
    await this.context.audioWorklet.addModule(captureWorkletUrl);
    this.source = this.context.createMediaStreamSource(this.stream);
    this.tap = new AudioWorkletNode(this.context, processorName);
    this.tap.port.onmessage = (event: MessageEvent<CaptureBatch>) => {
      const { samples, rms } = event.data;
      this.chunks.push(samples);
      this.reportLevel(Math.min(1, rms * 12), options.onLevel);
      const now = performance.now();
      if (rms > 0.012) {
        this.heardSpeech = true;
        this.silentSince = now;
      } else if (this.heardSpeech && this.silentSince > 0 && now - this.silentSince >= (options.silenceDurationMs ?? 1_300)) {
        this.requestAutoStop(options.onAutoStop);
      }
    };
    this.source.connect(this.tap);
    /*
     * Connected onward so the graph keeps pulling it, and silent because `process` never writes
     * to its outputs — what reaches the speakers is the zeroes the audio thread hands it.
     */
    this.tap.connect(this.context.destination);
    await this.context.resume();
    this.stopTimer = globalThis.setTimeout(() => this.requestAutoStop(options.onAutoStop), options.maxDurationMs ?? 20_000);
  }

  public async stop(): Promise<Float32Array> {
    if (this.stopTimer !== undefined) globalThis.clearTimeout(this.stopTimer);
    if (this.tap) this.tap.port.onmessage = null;
    this.tap?.disconnect();
    this.source?.disconnect();
    this.stream?.getTracks().forEach((track) => track.stop());
    await this.context?.close().catch(() => undefined);
    const merged = merge(this.chunks);
    const result = resample(merged, this.inputSampleRate, targetSampleRate);
    this.stream = undefined;
    this.context = undefined;
    this.source = undefined;
    this.tap = undefined;
    this.chunks = [];
    this.heardSpeech = false;
    this.silentSince = 0;
    this.stopTimer = undefined;
    this.autoStopped = false;
    this.reportedLevel = -1;
    return result;
  }

  /**
   * The meter, quantized before it is reported.
   *
   * Capture is off the main thread now, so a dropped buffer is no longer the risk it was — but
   * every level still lands in React state that a good part of the shell subscribes to, and
   * repainting the desktop eight times a second to move a bar by a pixel is work nobody asked
   * for. Thirty-two steps is finer than a 90-pixel bar can show, and through silence or steady
   * speech it collapses the run of updates to none at all.
   */
  private reportLevel(level: number, onLevel?: (level: number) => void): void {
    if (onLevel === undefined) return;
    const step = Math.round(level * 32);
    if (step === this.reportedLevel) return;
    this.reportedLevel = step;
    onLevel(step / 32);
  }

  private requestAutoStop(callback?: () => void): void {
    if (this.autoStopped) return;
    this.autoStopped = true;
    callback?.();
  }
}

/**
 * Asks the browser for a graph that already runs at Whisper's rate.
 *
 * Where this is honoured — and it is, in current Chrome, Firefox and Safari — the conversion
 * from whatever the microphone runs at is done by the browser's own resampler, in native code,
 * properly filtered, and `resample` below has nothing left to do. Where it is not, the context
 * comes back at the device rate and the fallback path handles it. Either way it is worth
 * asking: the browser is better at this than we are.
 */
function openContext(): AudioContext {
  try {
    return new AudioContext({ sampleRate: targetSampleRate });
  } catch {
    return new AudioContext();
  }
}

function merge(chunks: readonly Float32Array[]): Float32Array {
  const output = new Float32Array(chunks.reduce((sum, chunk) => sum + chunk.length, 0));
  let offset = 0;
  for (const chunk of chunks) {
    output.set(chunk, offset);
    offset += chunk.length;
  }
  return output;
}

/** Zero crossings of the sinc kernel kept either side of each output sample. */
const kernelWidth = 12;

/**
 * Rate conversion that does not fold the top of the band back into the middle of it.
 *
 * This used to be linear interpolation, and at the 3:1 ratio a 48 kHz microphone actually
 * gives us that is not a small compromise. Throwing away two samples in three without first
 * removing what lives above the new Nyquist folds everything from 8 to 24 kHz straight back
 * down into 0–8 kHz, and a triangular kernel only suppresses it by about 13 dB. What lands in
 * the speech band is not noise that sounds like noise — it is a mirror image of the fricatives,
 * which is exactly the part of Persian that carries ش, چ, ژ and س apart from one another. The
 * mel spectrogram sees that as real, and no model can be asked to unpick it afterwards.
 *
 * A windowed sinc, evaluated at the fractional output positions with its cutoff at the *output*
 * Nyquist, is the textbook answer and gets the rejection down where it belongs. It is
 * one pass at the output rate rather than the input rate, so a twenty-second capture costs a
 * few tens of milliseconds — against a model load measured in seconds.
 */
export function resample(input: Float32Array, inputRate: number, outputRate: number): Float32Array {
  if (inputRate === outputRate || input.length === 0) return input;
  const ratio = inputRate / outputRate;
  const outputLength = Math.max(1, Math.round(input.length / ratio));
  const output = new Float32Array(outputLength);
  /*
   * Cutoff in cycles per input sample. Coming down, it is the output's own Nyquist — anything
   * above cannot be represented and must not be allowed to alias. Going up there is nothing to
   * remove, so the kernel is left at the input's Nyquist and only interpolates.
   */
  const cutoff = ratio > 1 ? 0.5 / ratio : 0.5;
  const halfWidth = Math.max(1, Math.ceil(kernelWidth * Math.max(1, ratio)));

  for (let index = 0; index < outputLength; index += 1) {
    const position = index * ratio;
    const first = Math.ceil(position) - halfWidth;
    let total = 0;
    let weightSum = 0;
    for (let tap = 0; tap <= halfWidth * 2; tap += 1) {
      const sourceIndex = first + tap;
      const sample = input[sourceIndex];
      if (sample === undefined) continue;
      const distance = sourceIndex - position;
      const weight = sinc(2 * cutoff * distance) * blackman(distance / halfWidth);
      total += sample * weight;
      weightSum += weight;
    }
    // Normalizing per sample rather than once: the fractional offset shifts which part of the
    // kernel is sampled, and an unnormalized window would ripple the gain across the output.
    output[index] = weightSum === 0 ? 0 : total / weightSum;
  }
  return output;
}

function sinc(value: number): number {
  if (value === 0) return 1;
  const scaled = Math.PI * value;
  return Math.sin(scaled) / scaled;
}

/** Blackman window over [-1, 1], which reaches zero at both ends so the kernel truncates cleanly. */
function blackman(position: number): number {
  if (position <= -1 || position >= 1) return 0;
  return 0.42 + 0.5 * Math.cos(Math.PI * position) + 0.08 * Math.cos(2 * Math.PI * position);
}
